import jsPDF from 'jspdf';

// ---------------------------------------------------------------------------
// Cyrillic-capable font loader
// ---------------------------------------------------------------------------
// jsPDF ships only base PDF fonts (Helvetica, Times, Courier) which do NOT
// support Cyrillic. We load Roboto (Regular + Bold + Italic) with a two-tier
// strategy: (1) try jsDelivr CDN with a short timeout to keep the initial
// app bundle small for most users, (2) if the CDN fetch fails or times out
// (blocked corporate network, ad-blocker, offline), fall back to the locally
// bundled TTF files served from /fonts/. Whichever source wins is cached
// in-memory for the rest of the session.
// ---------------------------------------------------------------------------

const FONT_FAMILY = 'Roboto';
const CDN_TIMEOUT_MS = 4000;

const CDN_SOURCES = {
  normal: 'https://cdn.jsdelivr.net/gh/googlefonts/roboto@main/src/hinted/Roboto-Regular.ttf',
  bold: 'https://cdn.jsdelivr.net/gh/googlefonts/roboto@main/src/hinted/Roboto-Bold.ttf',
  italic: 'https://cdn.jsdelivr.net/gh/googlefonts/roboto@main/src/hinted/Roboto-Italic.ttf'
};

// Same-origin fallback. Files live in /app/frontend/public/fonts/ and are
// served by the frontend static host at ${PUBLIC_URL}/fonts/*.ttf.
const LOCAL_SOURCES = {
  normal: `${process.env.PUBLIC_URL || ''}/fonts/Roboto-Regular.ttf`,
  bold: `${process.env.PUBLIC_URL || ''}/fonts/Roboto-Bold.ttf`,
  italic: `${process.env.PUBLIC_URL || ''}/fonts/Roboto-Italic.ttf`
};

let fontRegistrationPromise = null;
const fontCache = {};

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function fetchAsBase64(url, { timeoutMs } = {}) {
  const controller = new AbortController();
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const buffer = await response.arrayBuffer();
    return arrayBufferToBase64(buffer);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function loadOneStyle(style) {
  // Try CDN with timeout first — keeps initial network fast, uses public cache.
  try {
    return await fetchAsBase64(CDN_SOURCES[style], { timeoutMs: CDN_TIMEOUT_MS });
  } catch (cdnErr) {
    console.warn(`Roboto ${style} CDN failed (${cdnErr.message || cdnErr}), falling back to local bundle.`);
    // Local fallback — same-origin, no CORS, no timeout needed.
    return await fetchAsBase64(LOCAL_SOURCES[style]);
  }
}

async function ensureFontsRegistered(pdf) {
  if (!fontRegistrationPromise) {
    fontRegistrationPromise = (async () => {
      const styles = Object.keys(CDN_SOURCES);
      const results = await Promise.all(
        styles.map(async (style) => [style, await loadOneStyle(style)])
      );
      for (const [style, b64] of results) {
        fontCache[style] = b64;
      }
    })().catch((err) => {
      // Reset so future export attempts can retry after transient failures.
      fontRegistrationPromise = null;
      throw err;
    });
  }
  await fontRegistrationPromise;

  for (const [style, b64] of Object.entries(fontCache)) {
    const filename = `Roboto-${style}.ttf`;
    pdf.addFileToVFS(filename, b64);
    pdf.addFont(filename, FONT_FAMILY, style === 'bold' ? 'bold' : style === 'italic' ? 'italic' : 'normal');
  }
}


// AdForge brand colors (WCAG AA compliant on white)
const COLORS = {
  primary: [90, 80, 229],         // #5A50E5 (5.65:1 on white, AA for normal text)
  accent: [108, 99, 255],          // #6C63FF light indigo
  text: [15, 15, 20],
  muted: [75, 85, 99],             // #4B5563 (7.56:1 on white, AAA)
  border: [229, 231, 235],
  background: [255, 255, 255],
  primaryBg: [242, 241, 255]
};

const PAGE_WIDTH = 210;   // A4 width in mm
const PAGE_HEIGHT = 297;  // A4 height in mm
const MARGIN = 20;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN * 2);

/**
 * Add a footer with page numbers to every page
 */
function addFooters(pdf) {
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(9);
    pdf.setTextColor(...COLORS.muted);
    pdf.setFont(FONT_FAMILY, 'normal');
    pdf.text('AI-generated content • Създадено с AIgnyx', MARGIN, PAGE_HEIGHT - 10);
    
    // Right: Page number
    pdf.text(
      `Page ${i} of ${pageCount}`,
      PAGE_WIDTH - MARGIN,
      PAGE_HEIGHT - 10,
      { align: 'right' }
    );
  }
}

/**
 * Draw the AdForge logo (simplified as text with a colored square)
 */
function drawLogo(pdf, x, y) {
  // Colored square as logo mark
  pdf.setFillColor(...COLORS.primary);
  pdf.roundedRect(x, y - 5, 6, 6, 1, 1, 'F');
  
  // "AIgnyx" text
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.text);
  pdf.setFont(FONT_FAMILY, 'bold');
  pdf.text('AIgnyx', x + 8, y);
}

/**
 * Add the cover page
 */
function addCoverPage(pdf, campaignName, platform) {
  const centerY = PAGE_HEIGHT / 2;
  
  // Background gradient effect (top accent bar)
  pdf.setFillColor(...COLORS.primary);
  pdf.rect(0, 0, PAGE_WIDTH, 8, 'F');
  
  // Logo at top
  drawLogo(pdf, MARGIN, 30);
  
  // "STORYBOARD" label
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.primary);
  pdf.setFont(FONT_FAMILY, 'bold');
  pdf.text('STORYBOARD', MARGIN, centerY - 40);
  
  // Campaign name (large)
  pdf.setFontSize(32);
  pdf.setTextColor(...COLORS.text);
  pdf.setFont(FONT_FAMILY, 'bold');
  const nameLines = pdf.splitTextToSize(campaignName || 'Untitled Campaign', CONTENT_WIDTH);
  pdf.text(nameLines, MARGIN, centerY - 20);
  
  // Separator line
  pdf.setDrawColor(...COLORS.primary);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, centerY + 10, MARGIN + 40, centerY + 10);
  
  // Platform badge
  if (platform) {
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.muted);
    pdf.setFont(FONT_FAMILY, 'normal');
    pdf.text(`Platform: ${platform.toUpperCase()}`, MARGIN, centerY + 22);
  }
  
  // Generated date
  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  pdf.setFontSize(10);
  pdf.setTextColor(...COLORS.muted);
  pdf.setFont(FONT_FAMILY, 'normal');
  pdf.text(`Generated: ${date}`, MARGIN, centerY + 30);
  
  // Bottom tagline
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.muted);
  pdf.setFont(FONT_FAMILY, 'italic');
  pdf.text(
    'AI-generated storyboard for short-form ads with viral potential',
    PAGE_WIDTH / 2,
    PAGE_HEIGHT - 30,
    { align: 'center' }
  );
}

/**
 * Add a single scene to the PDF (one scene per page)
 */
function addScenePage(pdf, scene) {
  pdf.addPage();
  
  let y = MARGIN + 5;
  
  // Small logo top-right
  drawLogo(pdf, MARGIN, y);
  y += 15;
  
  // Scene number badge (colored pill)
  pdf.setFillColor(...COLORS.primaryBg);
  pdf.roundedRect(MARGIN, y - 4, 30, 8, 2, 2, 'F');
  pdf.setFontSize(10);
  pdf.setTextColor(...COLORS.primary);
  pdf.setFont(FONT_FAMILY, 'bold');
  pdf.text(`SCENE ${scene.scene_number}`, MARGIN + 3, y + 1.5);
  
  // Duration badge (right-aligned)
  if (scene.duration) {
    pdf.setFillColor(...COLORS.primary);
    const durationWidth = pdf.getTextWidth(scene.duration) + 6;
    pdf.roundedRect(PAGE_WIDTH - MARGIN - durationWidth, y - 4, durationWidth, 8, 2, 2, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont(FONT_FAMILY, 'bold');
    pdf.setFontSize(9);
    pdf.text(scene.duration, PAGE_WIDTH - MARGIN - 3, y + 1.5, { align: 'right' });
  }
  
  y += 15;
  
  // Scene title (large)
  pdf.setFontSize(24);
  pdf.setTextColor(...COLORS.text);
  pdf.setFont(FONT_FAMILY, 'bold');
  const titleLines = pdf.splitTextToSize(scene.title || '', CONTENT_WIDTH);
  pdf.text(titleLines, MARGIN, y);
  y += titleLines.length * 9 + 8;
  
  // Divider
  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.3);
  pdf.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 10;
  
  // Description
  pdf.setFontSize(10);
  pdf.setTextColor(...COLORS.primary);
  pdf.setFont(FONT_FAMILY, 'bold');
  pdf.text('DESCRIPTION', MARGIN, y);
  y += 6;
  
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.text);
  pdf.setFont(FONT_FAMILY, 'normal');
  const descLines = pdf.splitTextToSize(scene.description || '', CONTENT_WIDTH);
  pdf.text(descLines, MARGIN, y);
  y += descLines.length * 5.5 + 10;
  
  // Two-column: Camera Angle + Visual Notes
  const colWidth = (CONTENT_WIDTH - 10) / 2;
  
  // Camera Angle box
  pdf.setFillColor(250, 250, 252);
  pdf.setDrawColor(...COLORS.border);
  pdf.roundedRect(MARGIN, y, colWidth, 40, 2, 2, 'FD');
  
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.primary);
  pdf.setFont(FONT_FAMILY, 'bold');
  pdf.text('CAMERA ANGLE', MARGIN + 4, y + 6);
  
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.text);
  pdf.setFont(FONT_FAMILY, 'normal');
  const cameraLines = pdf.splitTextToSize(scene.camera_angle || '—', colWidth - 8);
  pdf.text(cameraLines, MARGIN + 4, y + 14);
  
  // Visual Notes box
  const notesX = MARGIN + colWidth + 10;
  pdf.setFillColor(250, 250, 252);
  pdf.setDrawColor(...COLORS.border);
  pdf.roundedRect(notesX, y, colWidth, 40, 2, 2, 'FD');
  
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.primary);
  pdf.setFont(FONT_FAMILY, 'bold');
  pdf.text('VISUAL NOTES', notesX + 4, y + 6);
  
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.text);
  pdf.setFont(FONT_FAMILY, 'normal');
  const notesLines = pdf.splitTextToSize(scene.visual_notes || '—', colWidth - 8);
  pdf.text(notesLines, notesX + 4, y + 14);
  
  y += 50;
  
  // Placeholder for scene visual (if provided in future)
  if (scene.image_url) {
    // Reserved for future scene image support
  } else {
    // Visual placeholder card
    pdf.setFillColor(...COLORS.primaryBg);
    pdf.setDrawColor(...COLORS.primary);
    pdf.setLineDashPattern([2, 2], 0);
    pdf.roundedRect(MARGIN, y, CONTENT_WIDTH, 60, 3, 3, 'FD');
    pdf.setLineDashPattern([], 0);
    
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.primary);
    pdf.setFont(FONT_FAMILY, 'italic');
    pdf.text(
      'Scene visual — generate with Pika, Kling, or Runway',
      PAGE_WIDTH / 2,
      y + 32,
      { align: 'center' }
    );
  }
}

/**
 * Machine-readable AI provenance marker (EU AI Act Article 50 §2).
 * Emitted as PDF metadata Keywords / Subject so downstream tools can detect
 * AI origin without OCR or NLP.
 */
const AI_PROVIDER = 'AIgnyx';
const AI_MODEL = 'OpenAI GPT-5.2';
const AI_DISCLOSURE_URL = 'https://aignyx.com/privacy#ai';

function buildAiKeywords(contentType, campaignName) {
  return [
    'AI-Generated',
    'AIgnyx',
    'EU-AI-Act-Article-50',
    'Machine-Readable-Provenance',
    `Model=${AI_MODEL.replace(/\s+/g, '-')}`,
    `Provider=${AI_PROVIDER}`,
    `Type=${contentType}`,
    `Generated=${new Date().toISOString()}`,
    campaignName ? `Campaign=${campaignName.replace(/[,\n]/g, ' ').slice(0, 60)}` : null,
    `Notice=${AI_DISCLOSURE_URL}`
  ].filter(Boolean).join(', ');
}

/**
 * Main export function
 */
export async function exportStoryboardAsPDF(scenes, campaignName, platform) {
  if (!scenes || scenes.length === 0) {
    throw new Error('No scenes to export');
  }
  
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // EU AI Act Article 50 §2 — machine-readable provenance in the PDF Info
  // dictionary. Any PDF reader (Acrobat, pdftk, pdfinfo, exiftool) can extract
  // these fields without parsing page content.
  pdf.setProperties({
    title: `${campaignName || 'Untitled Campaign'} — AIgnyx Storyboard`,
    subject: 'AI-generated storyboard (EU AI Act Article 50 disclosure applies)',
    author: AI_PROVIDER,
    creator: `${AI_PROVIDER} (${AI_MODEL})`,
    keywords: buildAiKeywords('storyboard', campaignName)
  });

  // Register Cyrillic-capable Roboto font (Latin + Cyrillic + Greek).
  // Falls back silently to Helvetica if the CDN fetch fails so exports
  // never crash — Cyrillic will still be garbled in that edge case, but
  // Latin content will export cleanly.
  try {
    await ensureFontsRegistered(pdf);
    pdf.setFont(FONT_FAMILY, 'normal');
  } catch (err) {
    console.warn('Roboto font could not be loaded, PDF export may render Cyrillic incorrectly:', err);
  }
  
  // Cover page
  addCoverPage(pdf, campaignName, platform);
  
  // One page per scene
  for (const scene of scenes) {
    addScenePage(pdf, scene);
  }
  
  // Add page number footers
  addFooters(pdf);
  
  // Generate filename
  const safeName = (campaignName || 'untitled')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const dateStr = new Date().toISOString().slice(0, 10);
  const filename = `aignyx-storyboard-${safeName}-${dateStr}.pdf`;
  
  pdf.save(filename);
  return filename;
}
